import { motion } from 'framer-motion';
import { Navigation, Footer } from '../landing/LandingPage';

export function TermsPage() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <motion.div
          initial={false}
          animate="visible"
          variants={{
            visible: {
              transition: { staggerChildren: 0.1 },
            },
          }}
        >
          <motion.div variants={fadeInUp} className="mb-12">
            <h1 className="text-3xl md:text-4xl font-semibold text-text-primary mb-4">
              Terms of Service
            </h1>
            <p className="text-text-secondary">
              Last updated: 25 April 2026 &middot; Version 2.2
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="prose prose-slate max-w-none"
          >
            <div className="space-y-8 text-text-primary">
              <section>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Welcome to Wedsy. By accessing or using our website and services, you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, please do not use our service.
                </p>
                <div className="bg-primary/5 border border-primary/10 rounded-xl p-5 my-6">
                  <p className="text-text-primary leading-relaxed mb-0">
                    <strong>In short:</strong> These Terms cover how you can use Wedsy, what we&rsquo;re responsible for, and what you&rsquo;re responsible for. We&rsquo;ve tried to keep them clear and fair. If anything is unclear, please ask us at <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">hello@wedsy.co</a>.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">1. About Our Service</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Wedsy provides digital wedding invitation creation, guest list management, RSVP tracking, and related wedding communication services (&ldquo;the Service&rdquo;). The Service allows you to design and send digital invitations, manage guest responses, and coordinate your wedding communications through our platform at wedsy.co.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">2. Eligibility &amp; Account Registration</h2>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>You must be at least 18 years old (or the age of majority in your jurisdiction) to use our Service</li>
                  <li>You must provide accurate and complete information when creating an account</li>
                  <li>You may register using an email address and password, or by signing in with your Google account. If you use Google Sign-In, Google will share your name and email address with us to create or access your account</li>
                  <li>You are responsible for maintaining the confidentiality and security of your account credentials</li>
                  <li>One account per wedding — accounts are not transferable to other individuals or events</li>
                  <li>You must notify us promptly at <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">hello@wedsy.co</a> if you suspect unauthorised access to your account</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">3. Pricing &amp; Payment</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  All prices are displayed in GBP and are inclusive of UK VAT where applicable. For users outside the UK, your payment provider may apply currency conversion. Payments are processed securely through Stripe — we do not store your full card details.
                </p>

                <h3 className="text-lg font-semibold mb-3 mt-6">Wedding Invitation Package (one-off payment)</h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Our wedding invitation packages are purchased as a single, one-time payment. Upon successful payment you receive:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>Access to your digital wedding invitation for the duration of your wedding period</li>
                  <li>Full guest list management and RSVP tracking</li>
                  <li>All features included in your chosen package, as described at checkout</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  There are no recurring charges for invitation packages. Your invitation remains accessible to guests for as long as your account is active.
                </p>

                <h3 className="text-lg font-semibold mb-3 mt-6">Fair Use</h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Invitation packages are intended for personal use in connection with a single wedding or celebration. To ensure a quality experience for all users, the following fair use limits apply:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li><strong>Guests:</strong> Up to 1,000 guests per invitation</li>
                  <li><strong>Email sends:</strong> A reasonable number of invitation and reminder emails per guest over the duration of your event</li>
                  <li><strong>Bandwidth &amp; views:</strong> Invitation page traffic and asset usage should be consistent with a personal event. Unusually high or sustained traffic volumes fall outside the scope of a personal package</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Where usage materially exceeds these limits, or where a package is used for commercial purposes, we reserve the right to contact you to discuss your requirements. In many cases a move to our Planner Subscription will be a better fit. We will always contact you before taking any action on your account.
                </p>

                <h3 className="text-lg font-semibold mb-3 mt-6">Planner Subscription</h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Our planner subscription is designed for wedding professionals managing multiple events. Subscriptions are billed on a recurring basis (monthly or annually):
                </p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>Subscriptions renew automatically each billing cycle until cancelled</li>
                  <li>You can cancel at any time through your account settings — your access continues until the end of the current billing period, after which no further charges are made. All invitations in your account will remain live and accessible to guests until the end of the billing period; after that they will become inaccessible</li>
                  <li>Cancellation does not entitle you to a refund of the current period&rsquo;s fee unless required by applicable consumer law</li>
                  <li>Current pricing and included features are described on our pricing page</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 mt-6">Refund Policy</h3>
                <p className="text-text-secondary leading-relaxed mb-4">We want you to be happy with Wedsy. Our refund policy works as follows:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li><strong>Before sending invitations:</strong> Full refund within 14 days of purchase</li>
                  <li><strong>After sending invitations:</strong> Refunds provided at our discretion, as the digital service has been substantially performed</li>
                  <li><strong>Technical issues:</strong> Contact us within 14 days of purchase and we will work with you to resolve the issue or provide a refund</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  To request a refund, email <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">hello@wedsy.co</a>. Nothing in this section affects your statutory rights as a consumer.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">4. Your Consumer Rights</h2>
                <p className="text-text-secondary leading-relaxed mb-4">If you are a consumer, you have statutory rights that cannot be excluded or limited by these Terms:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li><strong>UK &amp; EU consumers:</strong> You have a statutory right to a 14-day cooling-off period for online purchases of digital content</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">5. Acceptable Use</h2>
                <p className="text-text-secondary leading-relaxed mb-4">You agree not to use Wedsy to:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>Send spam, unsolicited communications, or messages unrelated to your wedding</li>
                  <li>Upload illegal, harmful, threatening, abusive, defamatory, or offensive content</li>
                  <li>Impersonate others or misrepresent your identity</li>
                  <li>Attempt to access other users&rsquo; accounts or non-public areas of our systems</li>
                  <li>Interfere with or place an unreasonable burden on our services or infrastructure</li>
                  <li>Use automated systems (bots, scrapers, etc.) without prior written permission</li>
                  <li>Resell, sublicence, or redistribute our Service</li>
                  <li>Use the Service for any purpose other than managing a genuine wedding or celebration</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We reserve the right to suspend or terminate accounts that violate these rules.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">6. Your Content</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You retain full ownership of the content you upload to Wedsy, including photos, text, and guest information (&ldquo;Your Content&rdquo;). The underlying invitation designs, templates, layouts, illustrations, and visual assets provided by Wedsy remain our intellectual property — you are granted a personal, non-transferable licence to use them within the Service for your wedding.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  By using our Service, you grant us a limited, non-exclusive, royalty-free, worldwide licence to use, display, reproduce, and transmit Your Content solely as necessary to provide and improve the Service.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  This licence ends when you delete Your Content or close your account. You are responsible for ensuring you have the right to use any content you upload.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">7. Guest Data Responsibility</h2>
                <p className="text-text-secondary leading-relaxed mb-4">When you add guests and send invitations through Wedsy:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>You act as the data controller for your guests&rsquo; personal data, and we act as the data processor — except where a guest explicitly opts in to receive marketing communications from Wedsy during the RSVP process, in which case Wedsy acts as an independent data controller for that purpose only</li>
                  <li>You confirm that you have an appropriate legal basis to contact these individuals</li>
                  <li>You are responsible for the accuracy of guest contact information</li>
                  <li>Guests may opt out at any time via the unsubscribe link in every email</li>
                  <li>You agree not to upload guest data for purposes other than your wedding communications</li>
                </ul>
                <h3 className="text-lg font-semibold mb-3 mt-6">Dietary &amp; allergy information</h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Where you enable collection of dietary preferences or allergy information from your guests:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>You are solely responsible for how this information is used, communicated to your venue or caterers, and kept secure</li>
                  <li>Wedsy does not verify, act upon, or accept any liability for the accuracy or completeness of dietary or allergy information submitted by guests</li>
                  <li>Where a guest submits allergy information, Wedsy requires them to check an explicit acknowledgement confirming that the information is accurate and that they are personally responsible for notifying you and your venue directly. This acknowledgement is recorded with a timestamp. However, the existence of this acknowledgement does not transfer any duty of care from you as host to Wedsy</li>
                  <li>RSVP allergy and dietary responses are a guide only and must not be relied upon as a substitute for direct confirmation with your guests and venue on the day. You agree to treat them accordingly</li>
                  <li><strong>Wedsy accepts no liability whatsoever</strong> for any health outcome, allergic reaction, or other consequence arising from reliance on allergy or dietary information collected through the RSVP process</li>
                  <li>Allergy information (not dietary preferences) is automatically deleted from Wedsy's systems 60 days after the event date as part of our data minimisation obligations. You are responsible for retaining any records you require before that date</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">8. Service Availability &amp; Force Majeure</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We strive to maintain high availability and aim for at least 99.5% uptime, but we cannot guarantee uninterrupted service. We may perform scheduled maintenance, modify features with reasonable notice, or suspend accounts that violate these Terms.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Neither party will be liable for any failure or delay caused by circumstances beyond reasonable control, including natural disasters, pandemics, acts of government, internet outages, cyberattacks, or failures of third-party providers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">9. Intellectual Property</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  The Wedsy name, logo, website design, templates, illustrations, and all other visual and functional elements are our intellectual property and are protected by copyright, trademark, and other laws.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You may not copy, modify, distribute, reverse-engineer, or create derivative works from our templates or branding without prior written permission.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
                <p className="text-text-secondary leading-relaxed mb-4">To the fullest extent permitted by applicable law:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind</li>
                  <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Our total aggregate liability is limited to the amount you paid us in the 12 months preceding the claim</li>
                  <li>We are not responsible for the actions or non-responses of your wedding guests</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  <strong>Nothing in these Terms excludes or limits our liability for:</strong> death or personal injury caused by our negligence; fraud or fraudulent misrepresentation; or any other liability that cannot be excluded under applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">11. Indemnification</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  To the extent permitted by applicable law, you agree to indemnify and hold Wedsy harmless from any claims, damages, losses, liabilities, costs, or expenses arising from your use of the Service, Your Content, your violation of these Terms, or your violation of any rights of a third party.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">12. Dispute Resolution</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We hope to resolve any issues informally. If you have a dispute, please contact us first at <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">hello@wedsy.co</a> and we will try to work things out within 30 days.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">If informal resolution is not successful:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li><strong>UK &amp; EEA users:</strong> You may refer the dispute to an approved ADR provider, or to the courts. EU consumers may also use the European Commission&rsquo;s Online Dispute Resolution platform</li>
                  <li><strong>All other users:</strong> Any dispute that cannot be resolved informally will be subject to the jurisdiction described in Section 15</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">13. Termination</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You may close your account at any time by contacting us or through your account settings. Upon closure:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>Your invitations will no longer be accessible to guests</li>
                  <li>Your data will be deleted in accordance with our Privacy Policy retention schedule</li>
                  <li>Any unused portion of a paid package is non-refundable (subject to your statutory rights)</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We may suspend or terminate your account if you materially breach these Terms. We also reserve the right to suspend or terminate any account at our sole discretion, with or without notice. Where possible, we will give you notice and an opportunity to remedy the breach.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">14. Changes to These Terms</h2>
                <p className="text-text-secondary leading-relaxed mb-4">We may update these Terms from time to time. When we make material changes:</p>
                <ul className="list-disc pl-6 space-y-2 text-text-secondary mb-4">
                  <li>We will notify you by email at least 14 days before the changes take effect</li>
                  <li>We will post a notice on our Service</li>
                  <li>The &ldquo;Last updated&rdquo; date and version number will be revised</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">15. Governing Law &amp; Jurisdiction</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  These Terms are governed by the laws of England and Wales.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  If you are a consumer in the EU, EEA, or UK, you will benefit from any mandatory provisions of the consumer protection laws in your country of residence. For non-consumer disputes, proceedings will be subject to the exclusive jurisdiction of the courts of England and Wales. For consumer disputes, you may bring proceedings in either the courts of England and Wales or the courts of your country of residence.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">16. Severability</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be modified to the minimum extent necessary to make it enforceable. The remaining provisions will continue in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">17. Entire Agreement</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  These Terms, together with our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>, constitute the entire agreement between you and Wedsy regarding your use of the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">18. Assignment</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations to an affiliate or in connection with a merger, acquisition, or sale of assets.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">19. Waiver</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Our failure to enforce any provision of these Terms does not constitute a waiver of our right to enforce that provision in the future.
                </p>
              </section>

              <section>
                <div className="bg-background-card border border-border rounded-xl p-6 mt-8">
                  <h3 className="text-lg font-semibold mb-3">Questions?</h3>
                  <p className="text-text-secondary leading-relaxed mb-2">
                    If you have questions about these Terms of Service, please contact us:
                  </p>
                  <p className="text-text-secondary leading-relaxed">
                    <strong>Email:</strong> <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">hello@wedsy.co</a><br />
                    <strong>Website:</strong> <a href="https://wedsy.co" className="text-primary hover:underline">wedsy.co</a>
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
